import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import V2Layout from '../components/V2Layout';
import V2Icon from '../components/V2Icon';
import V2FilterBar, { type V2FilterBarValues } from '../components/V2FilterBar';
import useV2Auth from '../hooks/useV2Auth';
import { topPlayersReducer } from '../../containers/TopPlayersPage/reducer';
import { selectLoadingData, selectTopPlayers } from '../../containers/TopPlayersPage/selectors';
import { findStats } from '../../containers/StatPage/actions';
import { pokerSitePostfix } from '../../containers/StatPage/utils';
import formatWinnings from '../../utils/formatWinnings';
import type { TopPlayerDto } from '@poker-stats/contracts';

type TopPlayersFilters = V2FilterBarValues & {
  pokersiteId: number;
};

const V2TopPlayers: React.FC = () => {
  const dispatch = useDispatch();
  const { user } = useV2Auth();
  const [compact, setCompact] = React.useState(false);

  const [filters, setFilters] = React.useState<TopPlayersFilters>({
    tableSizeId: undefined,
    limitId: 0,
    pokersiteId: 0,
    dateId: undefined,
    customDate: undefined,
  });

  const loadingData = useSelector(selectLoadingData);
  const topPlayers = useSelector(selectTopPlayers);

  const loadTopPlayers = React.useCallback(
    (nextFilters: TopPlayersFilters) => {
      dispatch(
        topPlayersReducer.actions.loadTopPlayers({
          tableSizeFilterValue: nextFilters.tableSizeId,
          limitFilterValue: nextFilters.limitId,
          dateFilterValue: nextFilters.dateId,
          customDateFilterValue: nextFilters.customDate,
          pokersiteFilterValue: nextFilters.pokersiteId,
        }),
      );
    },
    [dispatch],
  );

  React.useEffect(() => {
    loadTopPlayers(filters);
  }, [filters, loadTopPlayers]);

  const handleFindStats = React.useCallback(
    (player: TopPlayerDto) => {
      dispatch(
        findStats({
          playername: player.playername,
          pokersiteId: player.pokersite_id,
        }),
      );
    },
    [dispatch],
  );

  return (
    <V2Layout
      activeNav="top-players"
      compact={compact}
      onToggleCompact={() => setCompact((value) => !value)}
      userEmail={user?.email}
      breadcrumbs={
        <p className="breadcrumbs">
          Топ игроков <span>/</span> <strong>Рейтинг</strong>
        </p>
      }
      footerNote="Топ игроков"
    >
      <section className="player-heading player-heading--page">
        <div>
          <div className="player-title-line">
            <h1>Топ игроков</h1>
          </div>
          <p className="profile-description">Рейтинг по выбранным фильтрам и периоду</p>
        </div>
        <div className="profile-actions profile-actions--filters">
          <V2FilterBar
            tableSizeId={filters.tableSizeId}
            limitId={filters.limitId}
            dateId={filters.dateId}
            customDate={filters.customDate}
            pokersiteId={filters.pokersiteId}
            onTableSizeChange={(tableSizeId) => setFilters((state) => ({ ...state, tableSizeId }))}
            onLimitChange={(limitId) => setFilters((state) => ({ ...state, limitId }))}
            onDateChange={(dateId, customDate) =>
              setFilters((state) => ({ ...state, dateId, customDate }))
            }
            onPokersiteChange={(pokersiteId) => setFilters((state) => ({ ...state, pokersiteId }))}
          />
          <button
            type="button"
            className="icon-button"
            aria-label="Переключить компактный режим"
            aria-pressed={compact}
            onClick={() => setCompact((value) => !value)}
          >
            <span>
              <V2Icon name="compact" />
            </span>
          </button>
        </div>
      </section>

      <div className="view-row">
        <nav className="view-tabs" aria-label="Раздел топ игроков">
          <button type="button" aria-current="page">
            Рейтинг
          </button>
        </nav>
      </div>

      <section className="history-section">
        <div className="block-heading">
          <h2>Лидеры периода</h2>
          <span className="muted">
            {loadingData ? 'Загрузка…' : `${topPlayers?.length ?? 0} игроков`}
          </span>
        </div>
        {!loadingData && (!topPlayers || topPlayers.length === 0) ? (
          <p className="table-hint">Нет данных для выбранных фильтров.</p>
        ) : (
          <div className="table-scroll" tabIndex={0} role="region" aria-label="Топ игроков">
            <table className="history-table">
              <thead>
                <tr>
                  {['#', 'Игрок', 'Рум', 'Раздачи', 'bb/100', 'Результат'].map((title) => (
                    <th key={title} scope="col">
                      {title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(topPlayers ?? []).map((player, index) => (
                  <tr key={`${player.playername}-${player.pokersite_id}`}>
                    <td>{index + 1}</td>
                    <td>
                      <button
                        type="button"
                        className="text-link"
                        onClick={() => handleFindStats(player)}
                      >
                        {player.playername}
                      </button>
                    </td>
                    <td>
                      <span className="room-chip acid">
                        {pokerSitePostfix[player.pokersite_id]}
                      </span>
                    </td>
                    <td>{player.hands}</td>
                    <td>{player.bb100}</td>
                    <td className={player.winnings >= 0 ? 'positive' : 'negative'}>
                      {formatWinnings(player.winnings, 2, false)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </V2Layout>
  );
};

export default V2TopPlayers;
